import * as analytics from '@anm/analytic';
import api from '@anm/api';
import { Workspace } from '@anm/api/modules/team';
import {
  CreateFolderParams,
  DeleteFolderParams,
  GetByIdParams,
  GetByPathParams,
  GetRootFoldersParams,
  GetTreeParams,
  MoveFolderParams,
  RenameFolderParams
} from '@anm/api/modules/videoFolder';
import asyncEntity from '@anm/helpers/redux/asyncEntity';
import { call, delay, fork, put, select, take } from '@anm/helpers/saga/effects';
import { takeType } from '@anm/helpers/saga/typesafe-actions';

import { videoFolderActions, videoFolderSelectors } from '..';
import { appMetaSelectors } from '../../appMeta';
import { appNotificationActions } from '../../appNotifications';
import { userSelectors } from '../../user';
import { checkCurrentFolderByPath, findFolderInTree, isRootFolder, selectFolderTree } from '../selectors';

const createFolderAsync = asyncEntity(videoFolderActions.createFolderAsync, (params: CreateFolderParams) =>
  api().videoFolder.create(params)
);

const moveFolderAsync = asyncEntity(videoFolderActions.moveFolderAsync, (params: MoveFolderParams) =>
  api().videoFolder.move(params)
);

const renameFolderRequest = async (params: RenameFolderParams) => {
  await api().videoFolder.rename(params);
  return await api().videoFolder.getById({ folderId: params.folderId, workspace: params.workspace });
};
const renameFolderAsync = asyncEntity(videoFolderActions.renameFolderAsync, renameFolderRequest);

const fetchFolderByIdAsync = asyncEntity(videoFolderActions.fetchFolderByIdAsync, (params: GetByIdParams) =>
  api().videoFolder.getById(params)
);

const deleteFolderAsync = asyncEntity(videoFolderActions.deleteFolderAsync, (params: DeleteFolderParams) =>
  api().videoFolder.deleteFolder(params)
);

const fetchRootFolderAsync = asyncEntity(videoFolderActions.fetchAsync, (params: GetRootFoldersParams) =>
  api().videoFolder.getRoot(params)
);

const fetchFolderByPathAsync = asyncEntity(videoFolderActions.fetchAsync, (params: GetByPathParams) =>
  api().videoFolder.getByPath(params)
);

const fetchFolderTreeAsync = asyncEntity(videoFolderActions.fetchFolderTreeAsync, (params: GetTreeParams) =>
  api().videoFolder.getTree(params)
);

export function* getRequestUserOrTeam<U extends Function, T extends Function>(userRequest: U, teamRequest: T) {
  const workspace = yield* select(appMetaSelectors.selectWorkspace);
  return workspace === 'user' ? userRequest : teamRequest;
}

function* watchMoveFolder() {
  while (true) {
    const { payload } = yield* take(takeType(videoFolderActions.moveFolder));
    const workspace = yield* select(appMetaSelectors.selectWorkspace);
    const params = { workspace, ...payload };

    yield* fork(moveFolderAsync, params, params);
  }
}

function* renameFolderGuard(params: RenameFolderParams) {
  const findFolder = yield* select(findFolderInTree);

  const folder = findFolder(params.folderId);

  if (!folder || folder.name === params.name) return;

  yield* fork(renameFolderAsync, params, params);
}

function* watchRenameFolder() {
  while (true) {
    const { payload } = yield* take(takeType(videoFolderActions.renameFolder));
    const workspace = yield* select(appMetaSelectors.selectWorkspace);
    const params = { workspace, ...payload };

    analytics.trackFolderRename();

    yield* fork(renameFolderGuard, params);
  }
}

function* watchFetchFolderById() {
  while (true) {
    const { payload: folderId } = yield* take(takeType(videoFolderActions.fetchFolderById));
    const workspace = yield* select(appMetaSelectors.selectWorkspace);
    const params = { workspace, folderId };

    yield* fork(fetchFolderByIdAsync, params);
  }
}

function* watchDeleteFolder() {
  while (true) {
    const { payload } = yield* take(takeType(videoFolderActions.deleteFolder));
    const workspace = yield* select(appMetaSelectors.selectWorkspace);
    const params = { workspace, ...payload };

    yield* fork(deleteFolderAsync, params, params);
  }
}

function* loadRootFolder(isForceLoad: boolean) {
  const profile = yield* select(userSelectors.selectUserProfile);
  const workspace = yield* select(appMetaSelectors.selectWorkspace);
  const isRootAlreadyLoaded = yield* select(isRootFolder);

  if (!profile || (isRootAlreadyLoaded && !isForceLoad)) return;

  yield* fork(fetchRootFolderAsync, { workspace });
}

function* watchFetchRootFolder() {
  while (true) {
    const { payload } = yield* take(takeType(videoFolderActions.fetchRootFolder));
    // yield put(appNotificationActions.cleanNotifications()); //todo: check if still need
    yield* fork(loadRootFolder, !!payload);
  }
}

function* loadFolderByPath(path: string, isForceLoad: boolean) {
  const checkCurrentFolder = yield* select(checkCurrentFolderByPath);
  const workspace = yield* select(appMetaSelectors.selectWorkspace);
  const params = { workspace, path };
  const isFolderAlreadyLoaded = checkCurrentFolder(path);

  if (isFolderAlreadyLoaded && !isForceLoad) return;

  yield* fork(fetchFolderByPathAsync, params, params);
}

function* watchFetchFolderByPath() {
  while (true) {
    const {
      payload: { path, isForceLoad }
    } = yield* take(takeType(videoFolderActions.fetchFolderByPath));

    yield put(appNotificationActions.cleanNotifications());

    yield* fork(loadFolderByPath, path, isForceLoad);
  }
}

export function* updateFolderPreview(folderId: number) {
  let currentFolder;
  let updatedCurrentFolder;
  let count = 1;
  const retry = 10;

  do {
    currentFolder = yield* select(state => videoFolderSelectors.selectFolderDataById(state, folderId));

    yield* delay(count * 1000);

    yield* put(videoFolderActions.fetchFolderById(folderId));
    yield* take(takeType(videoFolderActions.fetchFolderByIdAsync.success));

    updatedCurrentFolder = yield* select(state => videoFolderSelectors.selectFolderDataById(state, folderId));

    count++;
  } while (currentFolder?.preview === updatedCurrentFolder?.preview && count <= retry);
}

function* createFolderName(folderName?: string, parentId?: number) {
  if (folderName || !parentId) return folderName;

  const findFolderInTreeById = yield* select(findFolderInTree);
  const folders = findFolderInTreeById(parentId)!.folders;

  const name = 'New Folder';
  const reg = new RegExp(`${name} ([0-9]+)`);

  const lastNameNumber = folders.reduce((acc, folder) => {
    const matched = folder.name.match(reg);
    if (matched) {
      const FOLDER_NUMBER = 1;
      const currentNameNumber = parseInt(matched[FOLDER_NUMBER], 10) + 1;
      return currentNameNumber >= acc ? currentNameNumber : acc;
    }
    return acc;
  }, 1);

  return `${name} ${lastNameNumber}`;
}

function* watchCreateFolder() {
  while (true) {
    const { payload } = yield* take(takeType(videoFolderActions.createFolder));
    const workspace = yield* select(appMetaSelectors.selectWorkspace);
    const name = yield* call(createFolderName, payload.name, payload.parentId);
    const params = { name: name || '', workspace, ...payload };

    analytics.trackNewVideoFolder();

    yield* fork(createFolderAsync, params, params);
  }
}

const loadFolderTree = (() => {
  let prevWorkspace: Workspace;
  return function*(username: string) {
    const folderTree = yield* select(selectFolderTree);
    const workspace = yield* select(appMetaSelectors.selectWorkspace);
    const params = { workspace, username };

    if (folderTree && prevWorkspace === workspace) return;

    prevWorkspace = workspace;
    yield* fork(fetchFolderTreeAsync, params);
  };
})();

function* watchFetchFolderTree() {
  while (true) {
    const { payload: username } = yield* take(takeType(videoFolderActions.fetchFolderTree));

    yield* call(loadFolderTree, username);
  }
}

const folderSaga = () => [
  call(watchMoveFolder),
  call(watchRenameFolder),
  call(watchCreateFolder),
  call(watchDeleteFolder),
  call(watchFetchFolderTree),
  call(watchFetchRootFolder),
  call(watchFetchFolderById),
  call(watchFetchFolderByPath)
];

export default folderSaga;
